import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from '@mui/material';

// Encode a row-major Uint8Array (or boolean array) mask as COCO RLE
// (column-major alternating run-lengths, starting with a 0-run).
function encodeCOCORLE(mask, width, height) {
  const total = width * height;
  const col = new Uint8Array(total);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      col[x * height + y] = mask[y * width + x] ? 1 : 0;
    }
  }
  const counts = [];
  let cur = 0;
  let run = 0;
  for (let i = 0; i < total; i++) {
    if (col[i] === cur) {
      run++;
    } else {
      counts.push(run);
      cur = 1 - cur;
      run = 1;
    }
  }
  counts.push(run);
  return { counts, size: [height, width] };
}

// Pull the decoded mask for `objectId` out of objectMasks; accept the multiple
// shapes that have ended up in objectMasksRef across code paths.
function resolveMask(objectMasks, objectId) {
  if (!objectMasks) return null;
  const entry = objectMasks[objectId] ?? objectMasks[String(objectId)];
  if (!entry) return null;
  if (entry.mask && entry.width && entry.height) {
    return { mask: entry.mask, width: entry.width, height: entry.height };
  }
  if (entry.data && entry.size) {
    return { mask: entry.data, width: entry.size[1], height: entry.size[0] };
  }
  if (entry instanceof Uint8Array && entry.width && entry.height) {
    return { mask: entry, width: entry.width, height: entry.height };
  }
  return null;
}

/**
 * Multi-exemplar Find-All-Similar.
 *
 * Every existing object in the Colonies panel becomes an exemplar. The
 * backend embeds each exemplar with DINOv2 and keeps candidates whose MAX
 * cosine similarity to *any* exemplar exceeds the threshold. So clicking
 * a handful of representative colonies before opening this dialog directly
 * widens the matching set.
 */
export default function FindSimilarDialog({
  open,
  onClose,
  onRun,
  objectMeta,
  objectMasks,
}) {
  const objectIds = useMemo(() => {
    const ids = new Set();
    Object.keys(objectMeta || {}).forEach((k) => {
      const n = Number(k);
      if (Number.isFinite(n)) ids.add(n);
    });
    Object.keys(objectMasks || {}).forEach((k) => {
      const n = Number(k);
      if (Number.isFinite(n)) ids.add(n);
    });
    return [...ids]
      .filter((n) => resolveMask(objectMasks, n))
      .sort((a, b) => a - b);
  }, [objectMeta, objectMasks]);

  const [threshold, setThreshold] = useState(0.65);
  const [margin, setMargin] = useState(0.10);
  const [wShape, setWShape] = useState(0.40);
  const [wColour, setWColour] = useState(0.20);
  const [wEmbed, setWEmbed] = useState(0.40);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleRun = () => {
    if (objectIds.length === 0) {
      setError('No existing colonies to use as exemplars. Click on at least one colony first.');
      return;
    }
    try {
      const exemplar_rles = [];
      const skipped = [];
      for (const id of objectIds) {
        const resolved = resolveMask(objectMasks, id);
        if (!resolved) { skipped.push(id); continue; }
        exemplar_rles.push(encodeCOCORLE(resolved.mask, resolved.width, resolved.height));
      }
      if (exemplar_rles.length === 0) {
        setError(`No usable masks across ${objectIds.length} object(s).`);
        return;
      }
      onRun({
        pipeline_kwargs: {
          exemplar_rles,
          threshold,
          margin,
          w_shape: wShape,
          w_colour: wColour,
          w_embed: wEmbed,
        },
        usedExemplarCount: exemplar_rles.length,
        skippedExemplarCount: skipped.length,
      });
    } catch (e) {
      setError(`Failed to encode exemplar masks: ${e.message}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Find All Similar</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Every existing colony is used as an exemplar. Classical CV
            proposes candidate regions inside the dish, then each is scored
            against the best-matching exemplar using a fused similarity:
            <b> shape</b> (area, circularity, aspect, solidity),
            <b> colour</b> (mean LAB inside the blob), and
            <b> visual</b> embedding (DINOv2). Candidates whose combined
            score passes the threshold are kept.
          </Typography>

          {objectIds.length === 0 ? (
            <Alert severity="warning">
              No existing objects with masks. Click on at least one colony
              first, then re-open Find All Similar.
            </Alert>
          ) : (
            <Alert severity="info" icon={false} sx={{ bgcolor: 'rgba(144,202,249,0.10)' }}>
              {objectIds.length === 1
                ? '1 existing colony will be used as the exemplar.'
                : `${objectIds.length} existing colonies will be used as exemplars (max-similarity across all).`}
            </Alert>
          )}

          <Box>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Similarity threshold (cosine) — lower keeps more matches
            </Typography>
            <Slider
              size="small"
              value={threshold}
              min={0.50} max={0.95} step={0.01}
              onChange={(_, v) => setThreshold(v)}
              valueLabelDisplay="auto"
            />
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Exemplar crop padding (fraction of bbox)
            </Typography>
            <Slider
              size="small"
              value={margin}
              min={0} max={0.5} step={0.05}
              onChange={(_, v) => setMargin(v)}
              valueLabelDisplay="auto"
            />
          </Box>

          <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 0.5 }}>
              Feature weights — what counts as "similar"
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>Shape weight</Typography>
            <Slider size="small" value={wShape} min={0} max={1} step={0.05}
              onChange={(_, v) => setWShape(v)} valueLabelDisplay="auto" />
            <Typography variant="caption" sx={{ color: '#666' }}>Colour weight</Typography>
            <Slider size="small" value={wColour} min={0} max={1} step={0.05}
              onChange={(_, v) => setWColour(v)} valueLabelDisplay="auto" />
            <Typography variant="caption" sx={{ color: '#666' }}>Visual (DINOv2) weight</Typography>
            <Slider size="small" value={wEmbed} min={0} max={1} step={0.05}
              onChange={(_, v) => setWEmbed(v)} valueLabelDisplay="auto" />
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={objectIds.length === 0}
        >
          Find Similar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
